import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Page,
  Select,
  Text,
} from "@shopify/polaris";
import { useAuth } from "../App";
import {
  queueAccountKey,
  type QueueAccount,
} from "../../../packages/shared/shopify-queue/client";
import {
  comparisonRequest,
  createComparisonCommands,
  type ComparisonAction,
} from "../../../packages/shared/stock-comparison/client";
import type { ComparisonHistory } from "../../../packages/shared/stock-comparison/types";
import { ComparisonStart } from "../components/stock-comparison/ComparisonStart";
import { ComparisonResults } from "../components/stock-comparison/ComparisonResults";
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
const commands = new Map<string, ReturnType<typeof createComparisonCommands>>();
const storage = {
  get length() {
    return window.localStorage.length;
  },
  key: (i: number) => window.localStorage.key(i),
  getItem: (key: string) => window.localStorage.getItem(key),
  setItem: (key: string, value: string) =>
    window.localStorage.setItem(key, value),
  removeItem: (key: string) => window.localStorage.removeItem(key),
};
export default function StockComparison() {
  const { user, currentShop, permissions } = useAuth();
  const account = useMemo(
    () => ({
      userId: String(user?.id || ""),
      domain: currentShop?.domain || "",
      organizationId: currentShop?.organizationId,
    }),
    [user?.id, currentShop?.domain, currentShop?.organizationId],
  );
  const allowed = (p: string) =>
    permissions.includes("*") || permissions.includes(p);
  return (
    <Page
      title="Stock comparison"
      backAction={{ content: "Shopify updates", url: "/shopify-updates" }}
    >
      {!allowed("inventory.view") || !allowed("sync.status.view") ? (
        <Banner tone="critical">
          You need access to inventory and Shopify updates to view stock
          comparisons.
        </Banner>
      ) : !account.userId || !account.domain ? (
        <Banner>Select a company to compare stock.</Banner>
      ) : (
        <CompanyComparison
          key={queueAccountKey(account)}
          account={account}
          canPublish={allowed("inventory.sync")}
        />
      )}
    </Page>
  );
}
function CompanyComparison({
  account,
  canPublish,
}: {
  account: QueueAccount;
  canPublish: boolean;
}) {
  const key = queueAccountKey(account);
  if (!commands.has(key))
    commands.set(key, createComparisonCommands(base, account, storage));
  const command = commands.get(key)!;
  const state = useSyncExternalStore(command.subscribe, command.snapshot),
    [data, setData] = useState<ComparisonHistory | null>(null),
    [error, setError] = useState(""),
    [pages, setPages] = useState<number[]>([]),
    [refresh, setRefresh] = useState(0),
    [connectionId, setConnectionId] = useState(""),
    [params] = useSearchParams();
  const cursor = pages[pages.length - 1],
    runId = params.get("run");
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    void comparisonRequest<ComparisonHistory>(
      base,
      account,
      "?" + new URLSearchParams({...connectionId ? {connection_id: connectionId} : {}, ...cursor ? {cursor: String(cursor)} : {}}),
      { signal: abort.signal },
    )
      .then((value) => {
        if (!abort.signal.aborted) setData(value);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, cursor, refresh, connectionId]);
  useEffect(() => {
    if (!data || !["pending", "running"].includes(data.activeState || ""))
      return;
    const timer = window.setInterval(() => setRefresh((v) => v + 1), 10000);
    return () => window.clearInterval(timer);
  }, [data?.activeState]);
  const act = async (action: ComparisonAction) => {
    await command.submit(action);
    if (command.snapshot().success) {
      setPages([]);
      setRefresh((v) => v + 1);
    }
  };
  const locked = state.busy || !!state.pending;
  return (
    <BlockStack gap="400">
      {(error || state.error) && (
        <Banner tone="critical">{error || state.error}</Banner>
      )}
      {state.success && <Banner tone="success">{state.success}</Banner>}
      {state.pending && (
        <Banner tone="warning">
          <BlockStack gap="200">
            <Text as="p">
              A saved comparison action needs a confirmed reply. It stays saved
              after the browser closes.
            </Text>
            <Button
              loading={state.busy}
              onClick={() =>
                void command.retry().then(() => setRefresh((v) => v + 1))
              }
            >
              Retry saved comparison action
            </Button>
          </BlockStack>
        </Banner>
      )}
      {data && <Select label="Comparison store" value={data.selected_connection_id || ""}
        options={[{label: "Choose a store; show all comparisons", value: ""}, ...data.stores.map(s => ({label: s.domain + (s.connected ? "" : " (disconnected)"), value: s.id}))]}
        onChange={id => {setConnectionId(id); setPages([]);}} />}
      <ComparisonStart
        key={data?.selected_connection_id || "none"}
        account={account}
        connectionId={data?.selected_connection_id || null}
        disabled={locked || !data || !!data.activeId || !data.stores.some(s => s.id === data.selected_connection_id && s.connected)}
        onAction={act}
      />
      {runId && (
        <ComparisonResults
          key={runId}
          account={account}
          id={runId}
          refresh={refresh}
          locked={locked}
          canPublish={canPublish}
          onAction={act}
        />
      )}
      <Card>
        <BlockStack gap="300">
          <InlineStack gap="300">
            <Text as="h2" variant="headingMd">
              Saved stock comparisons
            </Text>
            <Button onClick={() => setRefresh((v) => v + 1)}>
              Refresh comparisons
            </Button>
          </InlineStack>
          {!data && !error && <Text as="p">Loading comparisons…</Text>}
          {data && (
            <>
              <Text as="p">{data.total} saved comparisons.</Text>
              {data.activeId && (
                <Button
                  url={
                    "/stock-comparison?run=" + encodeURIComponent(data.activeId)
                  }
                >
                  Open active comparison
                </Button>
              )}
              {!data.runs.length && (
                <Text as="p">No stock comparisons have been requested.</Text>
              )}
              {data.runs.map((run) => (
                <BlockStack key={run.id} gap="200">
                  <Text as="h3" variant="headingSm">
                    {run.domain} · {run.state}
                  </Text>
                  <Text as="p">
                    {run.created_at} UTC · {run.product_count} products ·{" "}
                    {run.matched} matched, {run.different} different,{" "}
                    {run.needs_review} need review, {run.pending} not checked.
                  </Text>
                  <Button
                    url={"/stock-comparison?run=" + encodeURIComponent(run.id)}
                  >
                    View comparison
                  </Button>
                </BlockStack>
              ))}
              <InlineStack gap="200">
                {!!pages.length && (
                  <Button onClick={() => setPages((p) => p.slice(0, -1))}>
                    Previous comparisons
                  </Button>
                )}
                {data.nextCursor && (
                  <Button
                    onClick={() => setPages((p) => [...p, data.nextCursor!])}
                  >
                    More comparisons
                  </Button>
                )}
              </InlineStack>
            </>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
